/* screens/workspace/ChatShell.jsx — экран активного диалога Ассистента (S5 + S6).
   Центральная область: ChatHeader + (ChatEmptyState | MessageList) + Composer.
   S6: inline-канвасы/feed-блоки рендерит MessageList — канвасы всегда инлайн в ленте.
   «Уточнить» из DraftEntityCard prefill-ит композер.
   ХЕНДОФФ → frontend/pages/workspace/Workspace.jsx. */

import React from 'react';
import { useShell } from '../../app/layouts/AppShell.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useBp } from '../../app/providers/BreakpointProvider.jsx';
import { useChat } from '../../app/providers/ChatProvider.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { ChatEmptyState } from '../../components/patterns/chat/ChatEmptyState.jsx';
import { ChatHeader } from '../../components/patterns/chat/ChatHeader.jsx';
import { Composer } from '../../components/patterns/chat/Composer.jsx';
import { MessageList } from '../../components/patterns/chat/MessageList.jsx';
import { ErrorState, ForbiddenState } from '../../components/patterns/States.jsx';
import { hooks } from '../../hooks/index.jsx';
import * as api from '../../lib/api/index.js';
import { RR } from '../../lib/router.jsx';
import { FALLBACK_SKILLS } from '../../lib/skillsFallback.js';
import { DEMO_TTK_PROMPT } from '../../app/onboarding/steps.jsx';
import { useOnboarding } from '../../app/onboarding/OnboardingProvider.jsx';
import { m1Session } from '../../app/onboarding/module1/module1Steps.jsx';

  const { useState, useEffect, useMemo, useCallback } = React;
  const { useParams, useNavigate, useSearchParams } = RR;
  const { useFakeQuery } = hooks;

  // FALLBACK_SKILLS импортируется из lib/skillsFallback — статический фолбэк галереи
  // скиллов, показывается, если /api/chats/skills/ недоступен.

  // structured-скиллы материализуют реальные сущности (ТТК/меню/отчёт) на ready —
  // перегенерация «на месте» их осиротила бы/задублировала, поэтому бэк отвечает
  // 409, а фронт прячет кнопку (зеркало серверного гейта).
  const REGEN_BLOCKED_SKILLS = new Set([
    'ttk-generate', 'menu-design', 'foodcost-optimize', 'abc-analyze', 'inventory-analyze',
  ]);

  // скелет ленты на время загрузки списка диалогов (вместо онбординга/спиннера)
  function ChatLoadingSkeleton() {
    return (
      <div className="chat-skeleton" aria-hidden="true">
        <div className="chat-skeleton__row chat-skeleton__row--user">
          <span className="chat-skeleton__bubble" style={{ width: '46%' }} />
        </div>
        <div className="chat-skeleton__row">
          <span className="chat-skeleton__avatar" />
          <span className="chat-skeleton__stack">
            <span className="chat-skeleton__line" style={{ width: '72%' }} />
            <span className="chat-skeleton__line" style={{ width: '88%' }} />
            <span className="chat-skeleton__line" style={{ width: '54%' }} />
          </span>
        </div>
        <div className="chat-skeleton__row chat-skeleton__row--user">
          <span className="chat-skeleton__bubble" style={{ width: '34%' }} />
        </div>
        <div className="chat-skeleton__row">
          <span className="chat-skeleton__avatar" />
          <span className="chat-skeleton__stack">
            <span className="chat-skeleton__line" style={{ width: '64%' }} />
            <span className="chat-skeleton__line" style={{ width: '40%' }} />
          </span>
        </div>
      </div>
    );
  }

  function ChatShell() {
    const { chatId } = useParams();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { activeWorkspace, user } = useAuth();
    const toast = useToast();
    const { bp, isMobile } = useBp();
    const chat = useChat();
    const onb = useOnboarding();
    const isBusiness = activeWorkspace.kind === 'business';

    const [composer, setComposer] = useState('');
    const [focusToken, setFocusToken] = useState(0);
    const [closed, setClosed] = useState({});       // {msgId:true} — закрытые канвасы

    // Реестр скиллов с боевого /api/chats/skills/ (адаптируется в карточную форму); при ошибке
    // — статический фолбэк (галерея EmptyState не должна пропадать из-за сбоя сети).
    const skillsQ = useFakeQuery('chat:skills', () => api.listSkills(), []);
    const skills = (skillsQ.data && skillsQ.data.length) ? skillsQ.data : FALLBACK_SKILLS;

    const activeChat = useMemo(() => (chat.chats || []).find((c) => c.id === chatId) || null, [chat.chats, chatId]);
    const skillHintFor = (c) => { if (!c) return null; const s = (skills || []).find((x) => x.slug === c.skill_slug); return s ? s.hint : null; };
    const canShare = isBusiness && activeChat && activeChat.can_manage;
    const canRegenerate = !!activeChat && !REGEN_BLOCKED_SKILLS.has(activeChat.skill_slug || '');
    // F41: повтор упавшего хода недоступен для structured-скиллов (они
    // материализуют сущность — бэк вернёт 409 retry_unsupported_for_skill).
    const canRetry = !!activeChat && !REGEN_BLOCKED_SKILLS.has(activeChat.skill_slug || '');

    // сброс состояния канвасов + ленивая загрузка сообщений диалога при смене.
    // F38: на открытии/refresh проверяем, не идёт ли серверный ход (reconnect) —
    // если идёт, провайдер поднимет спиннер и дотянет ответ из БД.
    useEffect(() => {
      setClosed({});
      if (chatId) {
        chat.ensureMessages(chatId); if (chat.checkInFlightTurn) chat.checkInFlightTurn(chatId);
        // авто-фокус композера при открытии существующего / создании нового диалога —
        // чтобы можно было сразу печатать запрос. На тач-устройствах пропускаем, иначе
        // экранная клавиатура всплывает поверх чтения истории (hover:none = ввод пальцем).
        if (!(typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches)) {
          setFocusToken((t) => t + 1);
        }
      }
    }, [chatId]);

    // prefill из ?prefill=
    useEffect(() => {
      const pf = params.get('prefill');
      if (pf) {
        setComposer(pf); setFocusToken((t) => t + 1);
        setParams((prev) => { const sp = new URLSearchParams(prev); sp.delete('prefill'); return sp; }, { replace: true });
      }
    }, [params]);

    // Из справки функции пользователь может подставить пример запроса в композер.
    const usePrompt = useCallback((text) => { setComposer(text); setFocusToken((t) => t + 1); }, []);
    // композер очищаем только когда отправка реально стартовала (не во время busy) — ввод не теряется
    const handleSubmit = useCallback((text, opts) => {
      if (chat.isBusy(chatId)) return;
      // onboarding=true → бэк не считает это сообщение в квоту и метит онбординг-чат
      chat.send(chatId, text, { ...opts, onboarding: !!(onb && onb.active) });
      setComposer('');
      // сигнал игровому онбордингу (тур ждёт реальной отправки сообщения)
      try { window.dispatchEvent(new CustomEvent('onb:sent')); } catch { /* noop */ }
    }, [chat, chatId, onb]);
    const regenerate = useCallback((message) => {
      if (chat.isBusy(chatId)) return;
      chat.regenerate(chatId, message);
    }, [chat, chatId]);
    // Модуль 2 демо-CJM: автозапуск квеста по ассистенту на первом заходе в чат
    // у флоу-пользователя (assistant_module ещё не пройден). Раз за сессию
    // (sessionStorage), квест не активен, Модуль 1 не открыт (не перебивать настройку).
    useEffect(() => {
      if (!onb || onb.active) return undefined;
      const f = user && user.onboarding_flow;
      if (!f || (f.completed_steps || []).includes('assistant_module')) return undefined;
      // Пока пространство не создано (шаг space), в чате должен вести Модуль 1,
      // а не квест: иначе посадка в /chat после логина хайджекается Модулем 2
      // и настройка не стартует вовсе (смоук W3, проба Y).
      if (!f.workspace_id) return undefined;
      if (m1Session.open) return undefined;
      let fired = false;
      try { fired = sessionStorage.getItem('restorium.onb_m2_auto') === '1'; } catch { /* noop */ }
      if (fired) return undefined;
      // задержка — первый кадр чата должен отрисоваться (спотлайту нужен композер)
      const t = setTimeout(() => {
        try { sessionStorage.setItem('restorium.onb_m2_auto', '1'); } catch { /* noop */ }
        onb.start(0);
      }, 400);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, onb && onb.active]);
    // страховка онбординга: пере-отправить демо-пример, если живой LLM завис/упал
    useEffect(() => {
      const h = () => { if (!chat.isBusy(chatId)) { chat.send(chatId, DEMO_TTK_PROMPT, { onboarding: true }); } };
      window.addEventListener('onb:resend', h);
      return () => window.removeEventListener('onb:resend', h);
    }, [chat, chatId]);
    const revise = useCallback((text) => { setComposer(text); setFocusToken((t) => t + 1); }, []);
    const committedDraft = useCallback(() => {}, []);
    const closeCanvas = useCallback((msgId) => setClosed((c) => ({ ...c, [msgId]: true })), []);

    const activeMsgs = activeChat ? (chat.msgs[activeChat.id] || []) : [];
    // онбординг-EmptyState — только при успешно загруженном пустом списке (не во время loading/error/403)
    const showEmpty = !chat.loading && !chat.error && !chat.forbidden && (!activeChat || activeMsgs.length === 0);
    const shell = useShell ? useShell() : null;

    return (
      <div className="chat-shell" data-bp={bp}>
        <section className="chat-shell__main">
          <ChatHeader
            chat={activeChat ? { ...activeChat, skill_hint: skillHintFor(activeChat) } : null}
            bp={bp} canShare={canShare}
            onRename={(t) => chat.renameChat(activeChat, t)}
            onShareToggle={(v) => chat.shareChat(activeChat, v)}
            onDelete={() => chat.deleteChat(activeChat, chatId)}
            onOpenNav={shell ? shell.openNav : null}
          />
          <div className="chat-conv">
            <div className="chat-conv__body">
              {chat.forbidden
                ? <div className="chat-conv__state"><ForbiddenState body="У вас нет доступа к диалогам этого пространства. Обратитесь к администратору." /></div>
                : chat.error
                  ? <div className="chat-conv__state"><ErrorState body="Не удалось загрузить диалоги. Попробуйте ещё раз." onRetry={chat.reload} /></div>
                  : chat.loading
                    ? <ChatLoadingSkeleton />
                    : showEmpty
                      ? <ChatEmptyState skills={skills} onUsePrompt={usePrompt} />
                      : <MessageList
                          messages={activeMsgs} onRegenerate={canRegenerate ? regenerate : null} onRevise={revise} onCommittedDraft={committedDraft}
                          onRetryTurn={canRetry ? ((m) => chat.retryTurn(chatId, m && m.id)) : null}
                          streamingId={chat.streamingIdFor(chatId)} chatId={chatId}
                          closedCanvases={closed} onCloseCanvas={closeCanvas} />}
            </div>
            <Composer
              value={composer} onChange={setComposer} onSubmit={handleSubmit}
              busy={chat.isBusy(chatId)} onStop={() => chat.cancel(chatId)} focusToken={focusToken} isMobile={isMobile}
            />
          </div>
        </section>
      </div>
    );
  }

export { ChatShell as ChatScreen };
export { ChatShell };
